import { types, flow, Instance } from 'mobx-state-tree';

import * as CompaniesAPI from '@src/services/companies';
import { renameObjectProperties, deleteEmptyArrays } from '@src/helpers';
import { SimpleApiStore } from '@src/features/common';
import { DocumentsStore } from '@src/features/employer/documents';
import { toast } from 'src/components';

const ActivityModel = types.model('ActivityModel', {
  id: types.identifierNumber,
  name: types.string,
  industry: types.maybeNull(types.string),
  desc_type: types.maybeNull(types.string),
  desc_text: types.maybeNull(types.string),
  desc_image_url: types.maybeNull(types.string),
  desc_video: types.maybeNull(types.string)
});

interface ILocation {
  id: number;
  country: null | string;
  city: null | string;
  street: null | string;
  postcode: null | string;
  lat: null | number;
  lon: null | number;
  address: null | string;
}

const CompanyModel = types.model('CompanyModel', {
  id: types.maybeNull(types.number),
  name: types.maybeNull(types.string),
  allow_external_url: types.maybeNull(types.boolean),
  slug: types.maybeNull(types.string),
  color: types.maybeNull(types.string),
  state: types.maybeNull(types.string),
  country: types.maybeNull(types.string),
  city: types.maybeNull(types.string),
  street: types.maybeNull(types.string),
  postcode: types.maybeNull(types.string),
  lat: types.maybeNull(types.number),
  lon: types.maybeNull(types.number),
  address: types.maybeNull(types.string),
  website: types.maybeNull(types.string),
  in_link: types.maybeNull(types.string),
  fb_link: types.maybeNull(types.string),
  tw_link: types.maybeNull(types.string),
  show_interest: types.maybeNull(types.boolean),
  show_map: types.maybeNull(types.boolean),
  interest_url: types.maybeNull(types.string),
  license: types.maybeNull(types.string),
  industry: types.maybeNull(types.string),
  logo_url: types.maybeNull(types.string),
  cover_picture_url: types.maybeNull(types.string),
  motto: types.maybeNull(types.string),
  employees_count: types.maybeNull(types.string),
  founded_in: types.maybeNull(types.number),
  parity_men: types.maybeNull(types.number),
  parity_women: types.maybeNull(types.number),
  activities: types.maybeNull(types.array(ActivityModel)),
  offices: types.maybeNull(types.frozen<ILocation[]>()),
  headquarters: types.maybeNull(types.frozen<ILocation>()),
  domains: types.frozen<{ id: string; domain: string }[]>([]),
  desc_text: types.maybeNull(types.string),
  desc_type: types.maybeNull(types.string),
  desc_video: types.maybeNull(types.string),
  desc_image_url: types.maybeNull(types.string),
  influencer_name: types.maybeNull(types.string),
  influencer_position: types.maybeNull(types.string),
  influencer_bio: types.maybeNull(types.string),
  influencer_avatar_url: types.maybeNull(types.string),
  influencer_image_url: types.maybeNull(types.string),
  influencer_desc_type: types.maybeNull(types.string),
  influencer_video: types.maybeNull(types.string),
  team_desc_type: types.maybeNull(types.string),
  team_desc_video: types.maybeNull(types.string),
  team_desc_image_url: types.maybeNull(types.string),
  allow_delete_candidates: types.maybeNull(types.boolean),
  timeframe_to_delete_candidates: types.maybeNull(types.number),
  allow_disclaimer: types.maybeNull(types.boolean),
  disclaimer_url: types.maybeNull(types.string),
  disclaimer_filename: types.maybeNull(types.string),
  disclaimer_updated_at: types.maybeNull(types.string),
  members: types.frozen([]),
  free_text_section_title: types.maybeNull(types.string),
  free_texts: types.frozen([]),
  media_contents: types.frozen([]),
  activeJobsCount: types.maybeNull(types.number),
  hasNewInterests: types.maybeNull(types.boolean),
  active: types.maybeNull(types.boolean),
  accessPolicies: types.frozen<{
    '/dashboard'?: boolean;
    '/talent_pool'?: boolean;
    '/edit'?: boolean;
    '/hiring_teams'?: boolean;
    '/partners_jobs'?: boolean;
    '/docs'?: boolean;
    '/preferences/career_page'?: boolean;
    '/preferences/partners'?: boolean;
    '/preferences/email_templates'?: boolean;
    '/preferences/tags'?: boolean;
  }>({})
});

export const CompanyStore = types
  .compose(
    SimpleApiStore,
    types.model('CompanyStore', {
      company: types.optional(CompanyModel, {}),
      documents: types.optional(DocumentsStore, {})
    })
  )
  .actions(self => {
    const fetchActions = {
      FRA_getCompany: flow(function*(...args) {
        const company = yield CompaniesAPI.getCompany(...args);
        self.company = { ...self.company, ...company };
      }),
      FRA_getCompanyActiveJobsCount: flow(function*(...args) {
        const { active_jobs_count } = yield CompaniesAPI.getCompanyActiveJobsCount(...args);
        self.company.activeJobsCount = active_jobs_count;
      }),
      FRA_getCompanyInterests: flow(function*(...args) {
        const { new_interests } = yield CompaniesAPI.getCompanyInterests(...args);
        self.company.hasNewInterests = new_interests;
      }),
      FRA_getCompanyAccessPolicies: flow(function*(...args) {
        const response = yield CompaniesAPI.getCompanyAccessPolicies(...args);
        self.company.accessPolicies = response;
      })
    };
    const restActions = {
      RA_uploadImage: flow(function*(...[payload, ...rest]: Parameters<typeof CompaniesAPI.uploadImage>) {
        const type = payload.file.get('image_type');
        const { photo_url } = yield CompaniesAPI.uploadImage(payload, ...rest);
        self.company[`${type}_url`] = photo_url;
      }),
      RA_removeImage: flow(function*(...[payload, ...rest]: Parameters<typeof CompaniesAPI.removeImage>) {
        yield CompaniesAPI.removeImage(payload, ...rest);
        self.company[`${payload.image_type}_url`] = null;
      }),
      RA_updateCompany: flow(function*(...args: Parameters<typeof CompaniesAPI.updateCompany>) {
        const { entity } = yield CompaniesAPI.updateCompany(...args);
        self.company = { ...self.company, ...entity };
      }),
      RA_updateDescription: flow(function*(...args): Generator<any, any, ICompany> {
        const { entity } = yield CompaniesAPI.updateDescription(...args);
        self.company = { ...self.company, ...entity };
      }),
      RA_updateInfluencer: flow(function*(...args): Generator<any, any, ICompany> {
        const { entity } = yield CompaniesAPI.updateInfluencer(...args);
        self.company = { ...self.company, ...entity };
      }),
      RA_uploadTeamMemberImage: flow(function*(...args: Parameters<typeof CompaniesAPI.uploadMemberImage>) {
        const response = yield CompaniesAPI.uploadMemberImage(...args);
        return response;
      }),
      RA_updateTeam: flow(function*(...args): Generator<any, any, ICompany> {
        const { team_desc_image_url, ...company } = yield CompaniesAPI.updateTeam(...args);
        self.company = { ...self.company, ...company };
      }),
      RA_updateHeadquarters: flow(function*(...args): Generator<any, any, ICompany> {
        const company = yield CompaniesAPI.updateHeadquarters(...args);
        self.company = { ...self.company, ...company };
      }),
      RA_updateFreeTexts: flow(function*(...args): Generator<any, any, ICompany> {
        const { entity } = yield CompaniesAPI.updateFreeTexts(...args);
        self.company = { ...self.company, ...entity };
      }),
      RA_uploadMediaImage: flow(function*(...args: Parameters<typeof CompaniesAPI.uploadMemberImage>) {
        const response = yield CompaniesAPI.uploadMediaImage(...args);
        return response;
      }),
      RA_updateMedia: flow(function*(...args): Generator<any, any, ICompany> {
        const { entity } = yield CompaniesAPI.updateMedia(...args);
        self.company = { ...self.company, ...entity };
      }),
      RA_updateOffices: flow(function*(...args): Generator<any, any, ICompany> {
        const { entity } = yield CompaniesAPI.updateOffices(...args);
        self.company = { ...self.company, ...entity };
      }),
      RA_updateSettings: flow(function*(...args): Generator<any, any, ICompany> {
        const { entity } = yield CompaniesAPI.updateSettings(...args);
        self.company = { ...self.company, ...entity };
      }),
      RA_uploadDisclaimer: flow(function*(...args: Parameters<typeof CompaniesAPI.uploadDisclaimer>) {
        const response = yield CompaniesAPI.uploadDisclaimer(...args);
        return response;
      }),
      RA_createActivity: flow(function*(...args: Parameters<typeof CompaniesAPI.createActivity>) {
        const { entity } = yield CompaniesAPI.createActivity(...args);
        self.company.activities.push(entity);
        return entity;
      }),
      RA_updateActivity: flow(function*(...args: Parameters<typeof CompaniesAPI.updateActivity>) {
        const { entity } = yield CompaniesAPI.updateActivity(...args);
        const index = self.company.activities.findIndex(activity => activity.id === entity.id);
        self.company.activities[index] = entity;
        return entity;
      }),
      RA_deleteActivity: flow(function*(...[payload, ...rest]: Parameters<typeof CompaniesAPI.deleteActivity>) {
        yield CompaniesAPI.deleteActivity(payload, ...rest);
        self.company.activities.replace(
          self.company.activities.filter(activity => String(activity.id) !== payload.activityId)
        );
      }),
      RA_uploadActivityImage: flow(function*(
        ...[payload, ...rest]: Parameters<typeof CompaniesAPI.uploadActivityImage>
      ) {
        const { photo_url } = yield CompaniesAPI.uploadActivityImage(payload, ...rest);
        const activityId = payload.file.get('id');
        const index = self.company.activities.findIndex(activity => String(activity.id) === activityId);
        self.company.activities[index].desc_image_url = photo_url;
      }),
      resetCandidateTest: flow(function*({ companyId, jobId, applicantId, testId }) {
        try {
          yield CompaniesAPI.resetCandidateTest({ companyId, jobId, applicantId, testId });
          toast.info('Success');
        } catch (error) {}
      })
    };

    return { ...fetchActions, ...restActions };
  })
  .views(self => ({
    get companyApiShape() {
      return deleteEmptyArrays(
        renameObjectProperties(self.company, {
          members: 'members_attributes',
          free_texts: 'free_texts_attributes',
          media_contents: 'media_contents_attributes',
          offices: 'offices_attributes'
        })
      );
    }
  }));

export type ICompanyStore = Instance<typeof CompanyStore>;
export type ICompany = Instance<typeof CompanyModel>;
